// store/dutyTrackingSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { dutyTrackingApi } from '../services/dutyTrackingApi';
import {
  DutyTrackingState,
  DutySchedule,
  DutyPeriod,
  CurrentDutyStatus,
  DutyTemplate,
  DutyAnalytics,
  CreateDutyScheduleRequest,
  UpdateDutyScheduleRequest,
  ScheduleUploadFormData,
  DutyScheduleListParams,
  CurrentDutyStatusParams,
  DutyAnalyticsParams,
  DutyStatusUpdateData,
  FileValidationResponse,
  BulkUploadResponse,
  ProcessingStatus
} from '../types/dutyTracking';

// Initial state
const initialState: DutyTrackingState = {
  schedules: [],
  dutyPeriods: [],
  currentStatuses: [],
  templates: [],
  analytics: [],
  loading: false,
  error: null,
  uploadStatus: 'idle',
  validationResults: {},
  selectedSchedule: null,
  filters: {}
};

// Async thunks
export const getDutySchedules = createAsyncThunk(
  'dutyTracking/getDutySchedules',
  async (params: DutyScheduleListParams | undefined, { rejectWithValue }) => {
    try {
      const response = await dutyTrackingApi.getDutySchedules(params);
      return response.results;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getDutySchedule = createAsyncThunk(
  'dutyTracking/getDutySchedule',
  async (scheduleId: number, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.getDutySchedule(scheduleId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createDutySchedule = createAsyncThunk(
  'dutyTracking/createDutySchedule',
  async (data: CreateDutyScheduleRequest, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.createDutySchedule(data);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateDutySchedule = createAsyncThunk(
  'dutyTracking/updateDutySchedule',
  async ({ scheduleId, data }: { scheduleId: number; data: UpdateDutyScheduleRequest }, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.updateDutySchedule(scheduleId, data);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteDutySchedule = createAsyncThunk(
  'dutyTracking/deleteDutySchedule',
  async (scheduleId: number, { rejectWithValue }) => {
    try {
      await dutyTrackingApi.deleteDutySchedule(scheduleId);
      return scheduleId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const approveDutySchedule = createAsyncThunk(
  'dutyTracking/approveDutySchedule',
  async (scheduleId: number, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.approveDutySchedule(scheduleId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const rejectDutySchedule = createAsyncThunk(
  'dutyTracking/rejectDutySchedule',
  async ({ scheduleId, reason }: { scheduleId: number; reason?: string }, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.rejectDutySchedule(scheduleId, reason);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const bulkUploadSchedules = createAsyncThunk(
  'dutyTracking/bulkUploadSchedules',
  async (data: ScheduleUploadFormData, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.bulkUploadSchedules(data);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const validateScheduleFile = createAsyncThunk(
  'dutyTracking/validateScheduleFile',
  async ({ file, departmentId, scheduleMonth }: { file: File; departmentId: number; scheduleMonth: string }, { rejectWithValue }) => {
    try {
      const response = await dutyTrackingApi.validateScheduleFile(file, departmentId, scheduleMonth);
      return { fileName: file.name, validationResult: response };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Current Duty Status thunks
export const getCurrentDutyStatuses = createAsyncThunk(
  'dutyTracking/getCurrentDutyStatuses',
  async (params: CurrentDutyStatusParams | undefined, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.getCurrentDutyStatuses(params);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCurrentDutyStatus = createAsyncThunk(
  'dutyTracking/getCurrentDutyStatus',
  async (staffId: number, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.getCurrentDutyStatus(staffId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateDutyStatus = createAsyncThunk(
  'dutyTracking/updateDutyStatus',
  async ({ staffId, data }: { staffId: number; data: DutyStatusUpdateData }, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.updateDutyStatus(staffId, data);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const goOnDuty = createAsyncThunk(
  'dutyTracking/goOnDuty',
  async ({ staffId, dutyPeriodId, departmentId, unitId }: { 
    staffId: number; 
    dutyPeriodId?: number; 
    departmentId?: number; 
    unitId?: number; 
  }, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.goOnDuty(staffId, dutyPeriodId, departmentId, unitId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const goOffDuty = createAsyncThunk(
  'dutyTracking/goOffDuty',
  async (staffId: number, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.goOffDuty(staffId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Duty Period thunks
export const getDutyPeriods = createAsyncThunk(
  'dutyTracking/getDutyPeriods',
  async (params: { scheduleId?: number; filters?: any } | undefined, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.getDutyPeriods(params?.scheduleId, params?.filters);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const startDuty = createAsyncThunk(
  'dutyTracking/startDuty',
  async ({ periodId, actualTime }: { periodId: number; actualTime?: string }, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.startDuty(periodId, actualTime);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const endDuty = createAsyncThunk(
  'dutyTracking/endDuty',
  async ({ periodId, actualTime }: { periodId: number; actualTime?: string }, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.endDuty(periodId, actualTime);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Templates thunks
export const getDutyTemplates = createAsyncThunk(
  'dutyTracking/getDutyTemplates',
  async (params: { department?: number; specialty?: number; is_active?: boolean } | undefined, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.getDutyTemplates(params);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Analytics thunks
export const getDutyAnalytics = createAsyncThunk(
  'dutyTracking/getDutyAnalytics',
  async (params: DutyAnalyticsParams, { rejectWithValue }) => {
    try {
      return await dutyTrackingApi.getDutyAnalytics(params);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const dutyTrackingSlice = createSlice({
  name: 'dutyTracking',
  initialState,
  reducers: {
    setSelectedSchedule: (state, action: PayloadAction<DutySchedule | null>) => {
      state.selectedSchedule = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<DutyTrackingState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    clearValidationResults: (state) => {
      state.validationResults = {};
    },
    updateScheduleLocally: (state, action: PayloadAction<DutySchedule>) => {
      const index = state.schedules.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.schedules[index] = action.payload;
      }
    },
    updateDutyStatusLocally: (state, action: PayloadAction<CurrentDutyStatus>) => {
      const index = state.currentStatuses.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.currentStatuses[index] = action.payload;
      } else {
        state.currentStatuses.push(action.payload);
      }
    }
  },
  extraReducers: (builder) => {
    // Get duty schedules
    builder
      .addCase(getDutySchedules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDutySchedules.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = action.payload;
      })
      .addCase(getDutySchedules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get single duty schedule
    builder
      .addCase(getDutySchedule.fulfilled, (state, action) => {
        state.selectedSchedule = action.payload;
      });

    // Create duty schedule
    builder
      .addCase(createDutySchedule.pending, (state) => {
        state.uploadStatus = 'loading';
      })
      .addCase(createDutySchedule.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.schedules.push(action.payload);
      })
      .addCase(createDutySchedule.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.error = action.payload as string;
      });

    // Update duty schedule
    builder
      .addCase(updateDutySchedule.fulfilled, (state, action) => {
        const index = state.schedules.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.schedules[index] = action.payload;
        }
        if (state.selectedSchedule?.id === action.payload.id) {
          state.selectedSchedule = action.payload;
        }
      });

    // Delete duty schedule
    builder
      .addCase(deleteDutySchedule.fulfilled, (state, action) => {
        state.schedules = state.schedules.filter(s => s.id !== action.payload);
        if (state.selectedSchedule?.id === action.payload) {
          state.selectedSchedule = null;
        }
      });

    // Approve duty schedule
    builder
      .addCase(approveDutySchedule.fulfilled, (state, action) => {
        const index = state.schedules.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.schedules[index] = action.payload;
        }
        if (state.selectedSchedule?.id === action.payload.id) {
          state.selectedSchedule = action.payload;
        }
      });

    // Reject duty schedule
    builder
      .addCase(rejectDutySchedule.fulfilled, (state, action) => {
        const index = state.schedules.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.schedules[index] = action.payload;
        }
        if (state.selectedSchedule?.id === action.payload.id) {
          state.selectedSchedule = action.payload;
        }
      });

    // Bulk upload schedules
    builder
      .addCase(bulkUploadSchedules.pending, (state) => {
        state.uploadStatus = 'loading';
      })
      .addCase(bulkUploadSchedules.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.schedules.push(...action.payload.created_schedules);
      })
      .addCase(bulkUploadSchedules.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.error = action.payload as string;
      });

    // Validate schedule file
    builder
      .addCase(validateScheduleFile.fulfilled, (state, action) => {
        state.validationResults[action.payload.fileName] = action.payload.validationResult;
      });

    // Current duty statuses
    builder
      .addCase(getCurrentDutyStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentDutyStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStatuses = action.payload;
      })
      .addCase(getCurrentDutyStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update duty status
    builder
      .addCase(updateDutyStatus.fulfilled, (state, action) => {
        const index = state.currentStatuses.findIndex(s => s.staff_member === action.payload.staff_member);
        if (index !== -1) {
          state.currentStatuses[index] = action.payload;
        }
      });

    // Go on/off duty
    builder
      .addCase(goOnDuty.fulfilled, (state, action) => {
        const index = state.currentStatuses.findIndex(s => s.staff_member === action.payload.staff_member);
        if (index !== -1) {
          state.currentStatuses[index] = action.payload;
        } else {
          state.currentStatuses.push(action.payload);
        }
      })
      .addCase(goOffDuty.fulfilled, (state, action) => {
        const index = state.currentStatuses.findIndex(s => s.staff_member === action.payload.staff_member);
        if (index !== -1) {
          state.currentStatuses[index] = action.payload;
        }
      });

    // Duty periods
    builder
      .addCase(getDutyPeriods.fulfilled, (state, action) => {
        state.dutyPeriods = action.payload;
      })
      .addCase(startDuty.fulfilled, (state, action) => {
        const index = state.dutyPeriods.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.dutyPeriods[index] = action.payload;
        }
      })
      .addCase(endDuty.fulfilled, (state, action) => {
        const index = state.dutyPeriods.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.dutyPeriods[index] = action.payload;
        }
      });

    // Templates
    builder
      .addCase(getDutyTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
      });

    // Analytics
    builder
      .addCase(getDutyAnalytics.fulfilled, (state, action) => {
        state.analytics = [action.payload];
      });
  }
});

export const {
  setSelectedSchedule,
  setFilters,
  clearFilters,
  clearError,
  clearValidationResults,
  updateScheduleLocally,
  updateDutyStatusLocally
} = dutyTrackingSlice.actions;

export default dutyTrackingSlice.reducer;

// Selectors
export const selectDutySchedules = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.schedules;
export const selectCurrentDutyStatuses = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.currentStatuses;
export const selectDutyPeriods = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.dutyPeriods;
export const selectDutyTemplates = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.templates;
export const selectDutyAnalytics = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.analytics;
export const selectSelectedSchedule = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.selectedSchedule;
export const selectDutyTrackingLoading = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.loading;
export const selectDutyTrackingError = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.error;
export const selectUploadStatus = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.uploadStatus;
export const selectValidationResults = (state: { dutyTracking: DutyTrackingState }) => state.dutyTracking.validationResults;

// Filtered selectors
export const selectSchedulesByStatus = (status: ProcessingStatus) => 
  (state: { dutyTracking: DutyTrackingState }) => 
    state.dutyTracking.schedules.filter(schedule => schedule.processing_status === status);

export const selectCurrentDutyStatusesByDepartment = (departmentId: number) =>
  (state: { dutyTracking: DutyTrackingState }) =>
    state.dutyTracking.currentStatuses.filter(status => status.current_department === departmentId);